import { useEffect, useState, ReactElement } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Paper from '@material-ui/core/Paper';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';

import api from '~/services/api';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import Loader from '~/ui/components/common/Loader';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import Button from '~/ui/components/common/Button';
import PageHeader from '~/ui/components/reusable/PageHeader';
import List from './List';
import Header from './Header';
import AddProgram from '~/ui/pages/Dashboard/popups/AddProgram';

import { IProgramList, IProgramsListFilters } from '~/services/api/programs/types';
import { UserRole } from '~/types';
import styles from './Programs.module.scss';

const Program = (): ReactElement => {
  const [isLoading, setIsLoading] = useState(true);
  const [showAddProgram, setShowAddProgram] = useState(false);
  const [filters, setFilters] = useState<IProgramsListFilters>({
    name: '',
  });
  const [deleteProgram, setDeleteProgram] = useState<IProgramList>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const userRole = useStoreState(state => state.auth.currentUser?.roleId);
  const { items: programs, pagination } = useStoreState(state => state.programs);

  const { showNotify, showError } = useStoreActions(actions => actions.snackbar);
  const { onGetPrograms, onGetMorePrograms } = useStoreActions(actions => actions.programs);

  const permissions = {
    canViewPage: [UserRole.ProgramAdmin, UserRole.SuperAdmin, UserRole.SystemAdmin].includes(
      userRole,
    ),
    canAddProgram: [UserRole.SuperAdmin, UserRole.SystemAdmin].includes(userRole),
    canRemoveProgram: userRole === UserRole.SystemAdmin,
  };

  const getMorePrograms = async () => {
    try {
      await onGetMorePrograms(filters);
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const getPrograms = async () => {
    try {
      setIsLoading(true);
      await onGetPrograms(filters);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const removeProgram = async () => {
    try {
      setIsDeleting(true);
      await api.programs.remove(deleteProgram.id);
      await getPrograms(); // refresh list after deleting
      setDeleteProgram(null);
      showNotify('Program removed');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    getPrograms();
  }, [filters]);

  if (!permissions.canViewPage) return null;

  return (
    <>
      <PageHeader title="Programs">
        {permissions.canAddProgram && (
          <div className={styles.addButton}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<AddCircleOutline />}
              onClick={() => setShowAddProgram(true)}
            >
              Add New
            </Button>
          </div>
        )}
      </PageHeader>
      <Paper className={styles.container} elevation={3}>
        <Header onSubmit={setFilters} />
        {isLoading ? (
          <Loader />
        ) : (
          <InfiniteScroll
            dataLength={programs.length}
            next={getMorePrograms}
            hasMore={pagination.hasMore}
            loader={<Loader />}
          >
            <List
              programs={programs}
              permissions={permissions}
              onRemoveProgram={setDeleteProgram}
            />
          </InfiniteScroll>
        )}
      </Paper>
      {showAddProgram && (
        <AddProgram
          onClose={() => {
            setShowAddProgram(false);
            getPrograms();
          }}
        />
      )}
      {deleteProgram && (
        <ConfirmModal
          title="Remove"
          description={`Are you sure you want to remove program ${deleteProgram.name}?`}
          isLoading={isDeleting}
          onClose={() => setDeleteProgram(null)}
          onConfirm={removeProgram}
        />
      )}
    </>
  );
};

export default Program;
